import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Heartbeat cadence — matches the AppStatusDot polling interval so the two
// loops feel coherent to a user watching dev-tools. Tuned for ~30s ejection
// latency on user-deletion.
const HEARTBEAT_MS = 30_000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading; false = unauth
  const [mfaPending, setMfaPending] = useState(null); // { mfa_token }
  // Tracks whether we've already shown the "your session has ended" toast,
  // so a burst of parallel 401s doesn't stack three identical toasts.
  const ejectingRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // -------------------------------------------------------------------------
  // Real-time session invalidation
  //
  // 1) `auth:expired` listener — fired by the global Axios interceptor in
  //    /lib/api.js whenever any protected endpoint returns 401. We wipe
  //    local auth state, toast the user, and force a hard redirect to /login.
  //
  // 2) Heartbeat — every HEARTBEAT_MS we hit /auth/me. If the user was
  //    deleted server-side, the 401 reply triggers the same interceptor
  //    and the listener below cleans up. This keeps the ejection latency
  //    bounded even when the user is sitting idle on a screen that makes
  //    no other API calls.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleExpired = (e) => {
      if (ejectingRef.current) return;
      // Only eject if we currently believe we *are* logged in. A 401 on the
      // landing/login page (e.g. background heartbeat racing logout) should
      // not show a toast or navigate.
      if (!user) return;
      ejectingRef.current = true;

      const detail = e?.detail?.reason;
      const isDeleted =
        typeof detail === "string" && /not\s*found/i.test(detail);

      setUser(false);
      setMfaPending(null);

      toast.error(
        isDeleted
          ? "Your account has been removed by an administrator."
          : "Your session has ended. Please sign in again."
      );

      // Hard redirect — guarantees every in-memory cache, axios queue, and
      // component tree is torn down, so the next paint starts from a clean
      // unauthenticated state.
      setTimeout(() => {
        if (typeof window !== "undefined") {
          window.location.assign("/login");
        }
      }, 350);
    };

    window.addEventListener("auth:expired", handleExpired);
    return () => window.removeEventListener("auth:expired", handleExpired);
  }, [user]);

  // Background heartbeat — only runs when a user is actually authenticated.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const ping = async () => {
      if (cancelled) return;
      try {
        await api.get("/auth/me");
      } catch {
        // 401 path is handled centrally by the axios interceptor +
        // auth:expired listener above. Other errors (network blips,
        // degraded backend) we deliberately swallow — the dot already
        // tells the user the API is down.
      }
    };

    const id = setInterval(ping, HEARTBEAT_MS);
    // Re-ping immediately on tab focus so a returning user is ejected
    // within ~1s instead of waiting up to a full interval.
    const onVisibility = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      setMfaPending({ mfa_token: data.mfa_token, email });
      return { mfa_required: true };
    }
    setUser(data.user);
    return { mfa_required: false };
  };

  const verifyMfa = async (code) => {
    const { data } = await api.post("/auth/mfa/verify", {
      mfa_token: mfaPending.mfa_token,
      code,
    });
    setUser(data.user);
    setMfaPending(null);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    // New gated sign-up — backend returns `pending_approval: true` and
    // does NOT issue an auth cookie. Caller is expected to render the
    // "awaiting approval" screen instead of navigating into /app.
    if (data?.pending_approval) {
      return { pending_approval: true };
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, mfaPending, login, verifyMfa, register, logout, refresh, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
